import { inject, Signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute, ActivatedRouteSnapshot, CanActivateFn, Router } from "@angular/router";
import { map } from "rxjs";

import { UpgradeImportType } from "../../models";
import { isPickerVendor } from "../import-source-select/picker-vendor-data";

// Rejects an invalid importType route param before the lazy chunk/component ever construct —
// redirects to redirectTo instead. A function builds the commands from the rejected route, for a
// path that holds a route param.
export function canActivateImportType(
  redirectTo: string | ((route: ActivatedRouteSnapshot) => string[]),
): CanActivateFn {
  return (route) => {
    const param = route.paramMap.get("importType");
    if (param && isPickerVendor(param)) {
      return true;
    }
    return inject(Router).createUrlTree(
      typeof redirectTo === "function" ? redirectTo(route) : [redirectTo],
    );
  };
}

// Step 1's preselected source on Back. Untrusted URL input, so anything but a picker vendor is dropped.
export function importSourceFromQuery(route: ActivatedRoute): UpgradeImportType | undefined {
  const param = route.snapshot.queryParamMap.get("source");
  return param && isPickerVendor(param) ? (param as UpgradeImportType) : undefined;
}

// Reactive to paramMap changes, not a one-time snapshot — stays correct if Angular reuses this
// route's component across a params-only navigation between two vendors.
export function importTypeFromRoute(route: ActivatedRoute): Signal<UpgradeImportType> {
  return toSignal(
    route.paramMap.pipe(map((params) => params.get("importType") as UpgradeImportType)),
    {
      initialValue: route.snapshot.paramMap.get("importType") as UpgradeImportType,
    },
  );
}
